import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { PA_COLUMNS, INTAKE_COLUMNS } from "@/constants/tableColumns";
import { SCOPE, isValidScope } from "@/constants/scope";

const LS_KEY_PA = "lh_table_columns_pa";
const LS_KEY_INTAKE = "lh_table_columns_intake";
const LS_KEY_PA_EXPANDED = "lh_table_expanded_pa";
const LS_KEY_INTAKE_VIEW = "lh_dashboard_view_intake";

function defaultPrefs(columns) {
  return columns.filter((c) => !c.fixed).map((c, i) => ({ key: c.key, visible: true, order: i }));
}

function loadPrefs(lsKey, defaults) {
  try {
    const raw = localStorage.getItem(lsKey);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    if (!Array.isArray(saved)) return null;

    const defaultKeys = defaults.filter((c) => !c.fixed).map((c) => c.key);
    const savedMap = new Map(saved.map((p) => [p.key, p]));

    // Keep only keys that still exist in defaults, preserve saved order/visibility
    const merged = [];
    let maxOrder = -1;
    for (const key of defaultKeys) {
      const existing = savedMap.get(key);
      if (existing) {
        merged.push({ key, visible: existing.visible, order: existing.order });
        if (existing.order > maxOrder) maxOrder = existing.order;
      }
    }

    // Append any new columns not in saved prefs
    for (const key of defaultKeys) {
      if (!savedMap.has(key)) {
        maxOrder++;
        merged.push({ key, visible: true, order: maxOrder });
      }
    }

    return merged;
  } catch {
    return null;
  }
}

function savePrefs(lsKey, prefs) {
  localStorage.setItem(lsKey, JSON.stringify(prefs));
}

function resolveColumns(prefs, allColumns) {
  const fixedCols = allColumns.filter((c) => c.fixed);
  const configurable = prefs
    .filter((p) => p.visible)
    .sort((a, b) => a.order - b.order)
    .map((p) => allColumns.find((c) => c.key === p.key))
    .filter(Boolean);
  return [...configurable, ...fixedCols];
}

export const useTableSettingsStore = defineStore("tableSettings", () => {
  const isDialogOpen = ref(false);
  // Scope toggle shared across Columns / Tags / Action Needed tabs in the Settings dialog.
  // Values come from @/constants/scope (SCOPE.PA | SCOPE.INTAKE). Seeded by the caller of open().
  const scope = ref(SCOPE.PA);

  const paColumnPrefs = ref(loadPrefs(LS_KEY_PA, PA_COLUMNS) || defaultPrefs(PA_COLUMNS));
  const intakeColumnPrefs = ref(loadPrefs(LS_KEY_INTAKE, INTAKE_COLUMNS) || defaultPrefs(INTAKE_COLUMNS));
  const paExpandedView = ref(localStorage.getItem(LS_KEY_PA_EXPANDED) === "true");
  const intakeViewMode = ref(localStorage.getItem(LS_KEY_INTAKE_VIEW) || "table");

  const paColumns = computed(() => resolveColumns(paColumnPrefs.value, PA_COLUMNS));
  const intakeColumns = computed(() => resolveColumns(intakeColumnPrefs.value, INTAKE_COLUMNS));

  function open(initialScope) {
    if (isValidScope(initialScope)) {
      scope.value = initialScope;
    }
    isDialogOpen.value = true;
  }

  function close() {
    isDialogOpen.value = false;
  }

  function setScope(s) {
    if (isValidScope(s)) scope.value = s;
  }

  function _getPrefs(tableKey) {
    return tableKey === SCOPE.PA ? paColumnPrefs : intakeColumnPrefs;
  }

  function _getLsKey(tableKey) {
    return tableKey === SCOPE.PA ? LS_KEY_PA : LS_KEY_INTAKE;
  }

  function setColumnVisibility(tableKey, colKey, visible) {
    const prefs = _getPrefs(tableKey);
    const item = prefs.value.find((p) => p.key === colKey);
    if (item) {
      item.visible = visible;
      savePrefs(_getLsKey(tableKey), prefs.value);
    }
  }

  function setColumnOrder(tableKey, orderedKeys) {
    const prefs = _getPrefs(tableKey);
    const prefMap = new Map(prefs.value.map((p) => [p.key, p]));
    orderedKeys.forEach((key, i) => {
      const p = prefMap.get(key);
      if (p) p.order = i;
    });
    savePrefs(_getLsKey(tableKey), prefs.value);
  }

  function resetColumns(tableKey) {
    const defaults = tableKey === SCOPE.PA ? PA_COLUMNS : INTAKE_COLUMNS;
    const prefs = _getPrefs(tableKey);
    prefs.value = defaultPrefs(defaults);
    localStorage.removeItem(_getLsKey(tableKey));
  }

  function togglePAExpandedView() {
    paExpandedView.value = !paExpandedView.value;
    localStorage.setItem(LS_KEY_PA_EXPANDED, String(paExpandedView.value));
  }

  function setPAExpandedView(val) {
    paExpandedView.value = val;
    localStorage.setItem(LS_KEY_PA_EXPANDED, String(val));
  }

  function setIntakeViewMode(mode) {
    intakeViewMode.value = mode;
    localStorage.setItem(LS_KEY_INTAKE_VIEW, mode);
  }

  return {
    isDialogOpen,
    scope,
    paColumnPrefs,
    intakeColumnPrefs,
    paColumns,
    intakeColumns,
    open,
    close,
    setScope,
    paExpandedView,
    togglePAExpandedView,
    setPAExpandedView,
    intakeViewMode,
    setIntakeViewMode,
    setColumnVisibility,
    setColumnOrder,
    resetColumns,
  };
});
